import { useState } from "react";
import client from "../lib/api";
import { toast } from "sonner";
import { Calculator, TrendUp, TrendDown, Warning, Sparkle } from "@phosphor-icons/react";
import PageHeader from "../components/PageHeader";

const DEFAULTS = { rate: 2500, miles: 800, fuel_price: 3.80, mpg: 6.5, driver_pay_per_mile: 0.60, other_costs: 100, lane: "" };

export default function CalculatorPage() {
  const [form, setForm] = useState(DEFAULTS);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const calc = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      const payload = {
        rate: Number(form.rate) || 0,
        miles: Number(form.miles) || 0,
        fuel_price: Number(form.fuel_price) || 0,
        mpg: Number(form.mpg) || 0,
        driver_pay_per_mile: Number(form.driver_pay_per_mile) || 0,
        other_costs: Number(form.other_costs) || 0,
        lane: form.lane || "",
      };
      const { data } = await client.post("/ai/profit-calc", payload);
      setResult(data);
    } catch {
      toast.error("Calculation failed");
    } finally { setLoading(false); }
  };

  const labelColor = {
    STRONG: { pill: "success", bg: "bg-[#10B981]/10", border: "border-[#10B981]/30", icon: <TrendUp size={22} weight="fill" color="#34d399" /> },
    MARGINAL: { pill: "warning", bg: "bg-[#F59E0B]/10", border: "border-[#F59E0B]/30", icon: <Warning size={22} weight="fill" color="#fbbf24" /> },
    SKIP: { pill: "error", bg: "bg-[#EF4444]/10", border: "border-[#EF4444]/30", icon: <TrendDown size={22} weight="fill" color="#f87171" /> },
  }[result?.label] || {};

  return (
    <div className="p-4 sm:p-8 max-w-[1400px]" data-testid="calculator-page">
      <PageHeader
        overline="AI PROFIT CALCULATOR"
        title="Should you take this load?"
        subtitle="Enter the rate and miles. AI tells you whether the load is worth it."
      />

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Inputs */}
        <form onSubmit={calc} className="lg:col-span-2 card-surface p-6 sm:p-8 space-y-5" data-testid="calc-form">
          <NumField label="Lane (optional)" value={form.lane} onChange={(v) => set("lane", v)} type="text" testid="calc-lane" placeholder="Dallas, TX → Atlanta, GA" />
          <div className="grid grid-cols-2 gap-4">
            <NumField label="Rate ($)" value={form.rate} onChange={(v) => set("rate", v)} testid="calc-rate" />
            <NumField label="Total miles" value={form.miles} onChange={(v) => set("miles", v)} testid="calc-miles" />
            <NumField label="Fuel price ($/gal)" value={form.fuel_price} onChange={(v) => set("fuel_price", v)} testid="calc-fuel-price" step="0.01" />
            <NumField label="Truck MPG" value={form.mpg} onChange={(v) => set("mpg", v)} testid="calc-mpg" step="0.1" />
            <NumField label="Driver pay ($/mi)" value={form.driver_pay_per_mile} onChange={(v) => set("driver_pay_per_mile", v)} testid="calc-driver-pay" step="0.01" />
            <NumField label="Other costs ($)" value={form.other_costs} onChange={(v) => set("other_costs", v)} testid="calc-other" />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="calc-submit-btn"
            className="w-full btn-primary rounded-full py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? "Calculating..." : "Calculate profit"} <Calculator size={16} weight="bold" />
          </button>
        </form>

        {/* Results */}
        <div className="lg:col-span-3">
          {result ? (
            <div className="space-y-4" data-testid="calc-results">
              <div className={`p-6 sm:p-8 rounded-lg border ${labelColor.border || "border-white/10"} ${labelColor.bg || "bg-[#141414]"}`}>
                <div className="flex items-start gap-4">
                  <div>{labelColor.icon || <Sparkle size={22} weight="fill" color="#FF5A1F" />}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`pill pill-${labelColor.pill || "primary"}`} data-testid="calc-verdict-label">{result.label}</span>
                      <span className="overline">AI VERDICT</span>
                    </div>
                    <p className="text-sm sm:text-base leading-relaxed text-neutral-100 whitespace-pre-wrap" data-testid="calc-verdict-text">
                      {result.verdict}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <ResultCell label="GROSS" value={`$${result.gross.toLocaleString()}`} />
                <ResultCell label="TOTAL COSTS" value={`$${result.total_costs.toLocaleString()}`} />
                <ResultCell label="NET PROFIT" value={`$${result.net.toLocaleString()}`} accent={result.net >= 0} negative={result.net < 0} />
                <ResultCell label="REVENUE/MI" value={`$${result.revenue_per_mile.toFixed(2)}`} />
                <ResultCell label="PROFIT/MI" value={`$${result.profit_per_mile.toFixed(2)}`} accent={result.profit_per_mile >= 0.50} negative={result.profit_per_mile < 0.30} />
                <ResultCell label="MARGIN" value={`${result.margin_pct.toFixed(1)}%`} />
              </div>

              <div className="card-surface p-6">
                <div className="overline mb-4">COST BREAKDOWN</div>
                <BreakdownBar label="Fuel" value={result.fuel_cost} max={result.total_costs} color="#F59E0B" />
                <BreakdownBar label="Driver pay" value={result.driver_pay} max={result.total_costs} color="#60a5fa" />
                <BreakdownBar label="Other" value={result.other_costs} max={result.total_costs} color="#a78bfa" />
              </div>
            </div>
          ) : (
            <div className="card-surface p-8 sm:p-12 text-center">
              <div className="w-14 h-14 rounded-2xl bg-[#FF5A1F]/10 border border-[#FF5A1F]/25 flex items-center justify-center mx-auto mb-5">
                <Calculator size={26} weight="regular" color="#FF5A1F" />
              </div>
              <h3 className="font-display text-2xl font-semibold">Ready when you are</h3>
              <p className="text-neutral-400 mt-3 text-sm max-w-md mx-auto leading-relaxed">
                Enter the load details on the left. AI runs the numbers, factors in fuel and driver pay,
                and tells you whether this load is worth booking.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, testid, type = "number", step, placeholder }) {
  return (
    <label className="block">
      <div className="overline mb-2">{label}</div>
      <input
        type={type}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        placeholder={placeholder}
        className="w-full bg-[#141414] border border-white/10 rounded-md px-3 py-2.5 text-sm text-white focus:border-[#FF5A1F]/60"
      />
    </label>
  );
}

function ResultCell({ label, value, accent, negative }) {
  return (
    <div className="card-surface p-4">
      <div className="overline mb-2">{label}</div>
      <div className={`font-mono text-xl font-semibold ${accent ? "text-[#10B981]" : negative ? "text-red-400" : "text-white"}`}>{value}</div>
    </div>
  );
}

function BreakdownBar({ label, value, max, color }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="mb-3 last:mb-0">
      <div className="flex items-center justify-between mb-1 text-xs">
        <span className="text-neutral-400">{label}</span>
        <span className="font-mono text-neutral-100">${value.toLocaleString()}</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}
